import React, { useEffect, useState } from "react";
import {
  ClipboardDocumentCheckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import ApiCustom from "../../utils/ApiCustom";
import {
  fetchCategoryByType,
  fetchCategoryByTypePublic,
} from "../../features/admin/CategorySlice";
import CustomTableComponent from "../../components/CustomTableComponent";
import { useDispatch } from "react-redux";

const getUserPermissions = () => {
  const permissions = localStorage.getItem("permissions");
  return permissions
    ? JSON.parse(permissions).map((p) => p.permissionSlug)
    : [];
};

const userId = localStorage.getItem("userId");
const hasPermission = (requiredPermission) => {
  if (userId == 1) {
    return true;
  }
  const userPermissions = getUserPermissions();
  return userPermissions.includes(requiredPermission);
};

const animation = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, y: 10, transition: { duration: 0.2 } },
};

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("vi-VN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "--";

// Modal chi tiết tối giản, accent nhẹ, content cuộn
const ActivityDetailModal = ({
  activity,
  registrationCount,
  joinedCount,
  onClose,
}) => {
  if (!activity) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4"
        {...animation}
      >
        <motion.div
          className="relative bg-white rounded-2xl max-w-7xl w-full p-8 shadow-xl border border-neutral-200"
          style={{ maxHeight: "90vh", overflow: "auto" }}
          initial={{ scale: 0.96 }}
          animate={{ scale: 1, transition: { duration: 0.2 } }}
          exit={{ scale: 0.96, transition: { duration: 0.15 } }}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
          <div className="mb-1 text-sm text-gray-500 font-medium">
            Danh mục:{" "}
            <span className="text-green-700 font-semibold">
              {activity.category?.name || "—"}
            </span>
          </div>
          <div className="flex items-center gap-3 mt-2 mb-6">
            <ClipboardDocumentCheckIcon className="h-6 w-6 text-green-500" />
            <h2 className="text-xl font-semibold text-gray-900">
              {activity.title}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-700">
            <div className="space-y-2">
              <div>
                Trạng thái:{" "}
                <span
                  className={`font-medium ${
                    activity.status === "published"
                      ? "text-green-700"
                      : "text-yellow-700"
                  }`}
                >
                  {activity.status === "published" ? "Đã duyệt" : "Chờ duyệt"}
                </span>
              </div>
              <div>Ngày tổ chức: {formatDate(activity.eventStartTime)}</div>
              <div>Ngày tạo: {formatDate(activity.createdAt)}</div>
            </div>
            <div className="space-y-2">
              <div>
                Bắt đầu đăng ký:{" "}
                <span className="text-blue-700 font-medium">
                  {formatDate(activity.regisStartTime)}
                </span>
              </div>
              <div>
                Kết thúc đăng ký:{" "}
                <span className="text-blue-700 font-medium">
                  {formatDate(activity.regisEndTime)}
                </span>
              </div>
              <div>
                Đăng ký:{" "}
                <span className="font-bold text-green-700">
                  {registrationCount ?? "--"}
                </span>
                &nbsp;|&nbsp; Tham gia:{" "}
                <span className="font-bold text-blue-700">
                  {joinedCount ?? "--"}
                </span>
              </div>
            </div>
          </div>
          <div className="mt-6 border-t pt-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">
              Nội dung chi tiết
            </h3>
            <div
              className="text-sm leading-relaxed text-gray-800 bg-gray-50 p-4 rounded-lg border"
              style={{
                maxHeight: 320,
                overflowY: "auto",
              }}
              dangerouslySetInnerHTML={{
                __html: activity.content || "<i>Không có nội dung.</i>",
              }}
            />
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Main Card
const ActivitiesCard = () => {
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [catModal, setCatModal] = useState(null);
  const [loadingCat, setLoadingCat] = useState(false);
  const [detailActivity, setDetailActivity] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [registrationCount, setRegistrationCount] = useState(null);
  const [joinedCount, setJoinedCount] = useState(null);

  const dispatch = useDispatch();

  useEffect(() => {
    setLoading(true);
    Promise.all([
      ApiCustom.get("/posts/?type=notification"),
      userId == 1 || hasPermission("OVERVIEW")
        ? dispatch(fetchCategoryByTypePublic("notification"))
        : dispatch(fetchCategoryByType("notification")),
    ])
      .then(([postRes, catRes]) => {
        const postList = postRes?.data?.data?.data || [];
        const cats = catRes?.payload || catRes?.data?.data || [];
        const counts = {};
        postList.forEach((p) => {
          const cid = p.categoryId || p.category?.id;
          if (cid) counts[cid] = (counts[cid] || 0) + 1;
        });

        setTotal(postList.length);
        setCategories(
          cats.map((cat) => ({
            ...cat,
            postCount: counts[cat.id] || 0,
          })),
        );
        setPosts(postList);
      })
      .finally(() => setLoading(false));
  }, [dispatch]);

  const openCatModal = (cat) => {
    setCatModal(cat);
    setLoadingCat(true);
    setTimeout(() => {
      setFilteredPosts(
        posts.filter(
          (p) =>
            (p.categoryId === cat.id ||
              (p.category && p.category.id === cat.id)) &&
            p.type === "notification",
        ),
      );
      setLoadingCat(false);
    }, 100);
  };

  const handleRowClick = async (activity) => {
    setDetailLoading(true);
    setDetailActivity(null);
    try {
      const res = await ApiCustom.get(`/posts/${activity.id}`);
      const detail = res?.data?.data || {};
      setDetailActivity(detail);

      const regRes = await ApiCustom.get("/eventRegistration-show");
      const regData = regRes?.data?.data?.data || [];
      const related = regData.filter((r) => r.postId === detail.id);
      setRegistrationCount(related.length);
      setJoinedCount(
        related.filter((r) => r.status === "Có" || r.status === "Có").length,
      );
    } catch {
      setDetailActivity(activity);
    } finally {
      setDetailLoading(false);
    }
  };

  const postHeaders = [
    { key: "title", label: "Tiêu đề" },
    {
      key: "status",
      label: "Trạng thái",
      render: (item) => (
        <span
          className={`px-3 py-1 rounded-full text-xs font-medium ${
            item.status === "published"
              ? "bg-green-50 text-green-700"
              : "bg-yellow-50 text-yellow-700"
          }`}
        >
          {item.status === "published" ? "Đã duyệt" : "Chờ duyệt"}
        </span>
      ),
    },
    {
      key: "category",
      label: "Danh mục",
      render: (item) => (
        <span className="text-gray-700">{item.category?.name}</span>
      ),
    },
    {
      key: "createdAt",
      label: "Ngày tạo",
      render: (item) =>
        item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "",
    },
  ];

  return (
    <div className="w-full">
      {/* Card newApple style */}
      <motion.div
        {...animation}
        whileHover={{ scale: 1.03 }}
        className="w-full shadow-md sm:w-auto flex flex-col items-center text-center p-5 rounded-2xl border border-gray-200 bg-white  transition-all cursor-pointer"
        onClick={() => setIsOpen(true)}
      >
        <div className="p-3 bg-green-50 rounded-full mb-3">
          <ClipboardDocumentCheckIcon className="h-6 w-6 text-green-600" />
        </div>
        <p className="text-lg text-black mb-1">Hoạt động</p>
        <p className="text-3xl font-semibold text-gray-800">
          {loading ? "..." : total}
        </p>
      </motion.div>

      {/* Modal danh mục hoạt động */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            {...animation}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center"
          >
            <motion.div
              {...animation}
              className="relative bg-white w-full max-w-5xl mx-4 rounded-xl p-6 shadow-xl border border-neutral-200"
            >
              <button
                onClick={() => setIsOpen(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
              <h2 className="text-xl font-semibold  mb-6 text-center">
                Các danh mục hoạt động
              </h2>
              {loading ? (
                <p className="text-gray-500">Đang tải...</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                  {categories.map((cat) => (
                    <motion.div
                      key={cat.id}
                      whileHover={{ scale: 1.04 }}
                      className="p-5 bg-white border border-green-50 rounded-xl hover:border-green-300 transition cursor-pointer flex flex-col items-center text-center"
                      onClick={() => openCatModal(cat)}
                    >
                      <div className="bg-green-50 rounded-full p-3 mb-2">
                        <ClipboardDocumentCheckIcon className="h-6 w-6 text-green-600" />
                      </div>
                      <h3 className="text-base font-semibold text-gray-800">
                        {cat.name}
                      </h3>
                      <p className="text-xs text-gray-400">{cat.slug}</p>
                      <p className="text-2xl font-bold text-green-700 mt-2">
                        {cat.postCount}
                      </p>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal danh sách hoạt động */}
      <AnimatePresence>
        {catModal && (
          <motion.div
            {...animation}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center"
          >
            <motion.div
              {...animation}
              className="relative bg-white w-full max-w-7xl mx-4 rounded-xl p-6 shadow-xl border border-neutral-200"
            >
              <button
                onClick={() => {
                  setCatModal(null);
                  setFilteredPosts([]);
                }}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
              <h2 className="text-lg font-semibold  mb-4">
                Danh sách: {catModal.name}
              </h2>
              {loadingCat ? (
                <p className="text-gray-500">Đang tải...</p>
              ) : (
                <CustomTableComponent
                  headers={postHeaders}
                  data={filteredPosts}
                  onRowClick={handleRowClick}
                />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal chi tiết hoạt động */}
      <ActivityDetailModal
        activity={detailActivity}
        loading={detailLoading}
        registrationCount={registrationCount}
        joinedCount={joinedCount}
        onClose={() => setDetailActivity(null)}
      />
    </div>
  );
};

export default ActivitiesCard;
